import { useCallback, useEffect } from "react";
import {
  ContextValue,
  SetDecisionParams,
} from "components/bookShuffle/useBookShuffle/types";
import { PreferenceValue } from "lib/api/onboarding";
import useWindowStorage from "utils/hooks/useWindowStorage";

const SHUFFLE_ACCEPT_STORAGE_KEY = "book-shuffle-accept-dialog";
const SHUFFLE_REJECT_STORAGE_KEY = "book-shuffle-reject-dialog";

export default function useSetDecision(context: ContextValue) {
  const {
    dispatch,
    stateRef,
    submitBookPreference,
    setBookSwipesComplete,
    currentIndex,
    books,
  } = context;

  const [_, { getItem, setItem }] = useWindowStorage();

  /**
   * Saves the user's decision for the current book,
   * calls the parent component's submitBookPreferences
   * and setBookSwipesComplete functions.
   */
  context.setDecision = useCallback(
    async (params: SetDecisionParams) => {
      const { preferenceValue } = params;

      const { books, currentIndex } = stateRef.current;

      const book = books[currentIndex];

      let showAcceptedDialog = false;
      let showRejectedDialog = false;

      // show the accept dialog if the user has not seen it yet
      const hasSeenAcceptedCardDialog = Boolean(
        getItem(SHUFFLE_ACCEPT_STORAGE_KEY)
      );
      if (
        preferenceValue == PreferenceValue.Like &&
        !hasSeenAcceptedCardDialog
      ) {
        setItem(SHUFFLE_ACCEPT_STORAGE_KEY, new Date().toISOString());
        showAcceptedDialog = true;
      }

      // show the reject dialog if the user has not seen it yet
      const hasSeenRejectedCardDialog = Boolean(
        getItem(SHUFFLE_REJECT_STORAGE_KEY)
      );
      if (
        preferenceValue == PreferenceValue.Dislike &&
        !hasSeenRejectedCardDialog
      ) {
        setItem(SHUFFLE_REJECT_STORAGE_KEY, new Date().toISOString());
        showRejectedDialog = true;
      }

      // save the decision to the state
      dispatch({
        type: "setDecision",
        payload: {
          preferenceValue,
          showAcceptedDialog,
          showRejectedDialog,
        },
      });

      // submit the decision to the to the outer component
      submitBookPreference?.({
        bookId: book.id,
        ownsBook: book.ownsBook,
        preferenceValue,
      });
    },
    [stateRef, dispatch, getItem, setItem, submitBookPreference]
  );

  // update the setBookSwipesComplete state when the currentIndex changes
  useEffect(() => {
    setBookSwipesComplete?.(currentIndex == books.length);
  }, [setBookSwipesComplete, currentIndex, books]);
}
